/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: we need to set the inner HTML for the code highlighting */
"use client";

import { useEffect, useState } from "react";
import { codeToHtml } from "shiki";
import { cn } from "../../lib/cn";
import { CodeWindowCopyButton } from "./copy-button";

export interface CodeWindowBaseProps {
  /** Code content to display */
  code: string;
  /** Programming language for syntax highlighting */
  language: string;
  /** Optional filename to display in header */
  filename?: string;
  /** Whether to show the header with window dots (default: true) */
  showHeader?: boolean;
  /** Whether to show the copy button in the header (default: false) */
  showCopy?: boolean;
  /**
   * Theme mode for the code window:
   *
   * **Theme Variables (adapts to your theme colors):**
   * - 'light': Always light appearance, uses theme variables
   * - 'dark': Always dark appearance, uses theme variables
   * - 'auto': Adapts to page light/dark mode, uses theme variables
   *
   * **Hardcoded (classic GitHub look):**
   * - 'github': Adapts to page light/dark mode, hardcoded GitHub colors
   */
  theme?: "dark" | "light" | "auto" | "github";
  /** Visual style: 'default' (border + radius + shadow), 'borderless' (no border/radius/shadow) */
  style?: "default" | "borderless";
  /** Additional className for the container */
  className?: string;
  /** Additional className for the code content area */
  contentClassName?: string;
}

/**
 * CodeWindowBase - Base syntax-highlighted code block
 *
 * Uses Shiki for syntax highlighting.
 * This is the base component - use sized variants for specific use cases.
 */

// Theme variable classes - used by 'light', 'dark', and 'auto' modes
// Uses bg-background so gradient overlays (from-background to-background/0) blend perfectly
const themedContainerClasses = "border-border bg-background";
const themedHeaderClasses = "bg-muted";
const themedDotsClasses = "bg-muted-foreground/30";
const themedFilenameClasses = "text-muted-foreground";
const themedCopyClasses =
  "text-muted-foreground hover:text-foreground hover:bg-muted";

// GitHub theme: hardcoded GitHub colors with dark: variants for light/dark switching
const githubContainerClasses =
  "border-[#d0d7de] bg-[#ffffff] dark:border-[#30363d] dark:bg-[#0d1117]";
const githubHeaderClasses = "bg-[#f6f8fa] dark:bg-[#161b22]";
const githubDotsClasses = "bg-[#d0d7de] dark:bg-[#484f58]";
const githubFilenameClasses = "text-[#656d76] dark:text-[#8b949e]";
const githubCopyClasses =
  "text-[#656d76] hover:text-[#24292f] hover:bg-[#d0d7de] dark:text-[#8b949e] dark:hover:text-[#e6edf3] dark:hover:bg-[#30363d]";

// Style variant classes (container)
// CRITICAL: overflow-hidden clips header to match rounded corners - DO NOT REMOVE
const styleClasses: Record<
  NonNullable<CodeWindowBaseProps["style"]>,
  string
> = {
  default: "rounded-[var(--radius)] border shadow-lg overflow-hidden",
  borderless: "",
};

// Style variant classes (content area)
const contentStyleClasses: Record<
  NonNullable<CodeWindowBaseProps["style"]>,
  string
> = {
  default: "",
  borderless: "p-2",
};

// Helper to get theme classes
function getThemeClasses(theme: "dark" | "light" | "auto" | "github") {
  if (theme === "github") {
    return {
      container: githubContainerClasses,
      header: githubHeaderClasses,
      dots: githubDotsClasses,
      filename: githubFilenameClasses,
      copy: githubCopyClasses,
    };
  }
  // All theme variable modes (dark, light, auto) use the same classes
  // The forcing is done via wrapper div with dark/light class
  return {
    container: themedContainerClasses,
    header: themedHeaderClasses,
    dots: themedDotsClasses,
    filename: themedFilenameClasses,
    copy: themedCopyClasses,
  };
}

export function CodeWindowBase({
  code,
  language,
  filename,
  showHeader = true,
  showCopy = false,
  theme = "auto",
  style = "default",
  className,
  contentClassName,
}: CodeWindowBaseProps) {
  const [darkHtml, setDarkHtml] = useState<string>("");
  const [lightHtml, setLightHtml] = useState<string>("");

  useEffect(() => {
    // Generate dark syntax highlighting for dark-appearing modes
    if (theme === "dark" || theme === "auto" || theme === "github") {
      codeToHtml(code, {
        lang: language,
        theme: "github-dark",
      }).then(setDarkHtml);
    }

    // Generate light syntax highlighting for light-appearing modes
    if (theme === "light" || theme === "auto" || theme === "github") {
      codeToHtml(code, {
        lang: language,
        theme: "github-light",
      }).then(setLightHtml);
    }
  }, [code, language, theme]);

  const themeClasses = getThemeClasses(theme);

  // Determine if we need to force a color mode via wrapper
  const forcedMode =
    theme === "dark" ? "dark" : theme === "light" ? "light" : null;

  const codeWindowContent = (
    <div
      className={cn(
        "relative flex w-full flex-col",
        // Force light/dark color mode on the root itself so theme variables
        // (bg-background/bg-muted) resolve correctly without an extra wrapper.
        // A wrapper would swallow layout classes from `className` (e.g. grid
        // col-span), so it must stay on the same element that receives className.
        forcedMode,
        styleClasses[style],
        themeClasses.container,
        className,
      )}
    >
      {/* Window header with dots - fixed height for consistency */}
      {showHeader && (
        <div
          className={cn(
            "relative flex h-11 items-center justify-center px-4",
            themeClasses.header,
          )}
        >
          <div className="absolute left-4 flex gap-1.5">
            <div className={cn("size-3 rounded-full", themeClasses.dots)} />
            <div className={cn("size-3 rounded-full", themeClasses.dots)} />
            <div className={cn("size-3 rounded-full", themeClasses.dots)} />
          </div>
          {filename ? (
            <span className={cn("text-xs", themeClasses.filename)}>
              {filename}
            </span>
          ) : (
            <span className="text-xs text-transparent select-none">&nbsp;</span>
          )}
          {showCopy && (
            <div className="absolute right-4">
              <CodeWindowCopyButton code={code} className={themeClasses.copy} />
            </div>
          )}
        </div>
      )}
      {/* Floating copy button when header is hidden */}
      {!showHeader && showCopy && (
        <div
          className={cn(
            "absolute z-10",
            style === "borderless" ? "top-4 right-4" : "top-2 right-2",
          )}
        >
          <CodeWindowCopyButton code={code} className={themeClasses.copy} />
        </div>
      )}
      {/* Code content */}
      <div
        className={cn(
          "flex-1 overflow-x-auto overflow-y-auto",
          contentStyleClasses[style],
          !showHeader && style === "default" && "p-2",
          contentClassName,
        )}
      >
        {theme === "dark" && (
          <div
            className="p-4 text-sm text-left [&_pre]:!bg-transparent [&_pre]:!p-0 [&_pre]:!m-0 [&_pre]:!text-left [&_pre]:!w-fit [&_pre]:!pr-4 [&_code]:!bg-transparent"
            dangerouslySetInnerHTML={{ __html: darkHtml }}
          />
        )}
        {theme === "light" && (
          <div
            className="p-4 text-sm text-left [&_pre]:!bg-transparent [&_pre]:!p-0 [&_pre]:!m-0 [&_pre]:!text-left [&_pre]:!w-fit [&_pre]:!pr-4 [&_code]:!bg-transparent"
            dangerouslySetInnerHTML={{ __html: lightHtml }}
          />
        )}
        {(theme === "auto" || theme === "github") && (
          <>
            <div
              className="p-4 text-sm text-left [&_pre]:!bg-transparent [&_pre]:!p-0 [&_pre]:!m-0 [&_pre]:!text-left [&_pre]:!w-fit [&_pre]:!pr-4 [&_code]:!bg-transparent dark:hidden"
              dangerouslySetInnerHTML={{ __html: lightHtml }}
            />
            <div
              className="p-4 text-sm text-left [&_pre]:!bg-transparent [&_pre]:!p-0 [&_pre]:!m-0 [&_pre]:!text-left [&_pre]:!w-fit [&_pre]:!pr-4 [&_code]:!bg-transparent hidden dark:block"
              dangerouslySetInnerHTML={{ __html: darkHtml }}
            />
          </>
        )}
      </div>
    </div>
  );

  return codeWindowContent;
}
